import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { addMinutes } from "date-fns";
import { useEffect, useRef } from "react";
import { LoadingSpinner } from "@/components/loading";
import { Button } from "@/components/ui";
import { queryClient } from "@/config/react-query";
import { useSelfSchedulePatientDetails } from "@/contexts/self-schedule-patient-details-context";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientClaim, useUpdateClaimedPatient } from "@/hooks/patient-claim/use-patient-claim";
import { getMarketingBundle } from "@/lib/marketing-attribution";
import { parseError } from "@/lib/parse-error";
import { computeTotalDuration, flattenServiceIds, resolveServiceOccurrences } from "@/lib/service-addon-utils";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { EventStatus } from "@/types/enums";
import { toPublicEventTiny } from "@/types/event";
import { SelfScheduleAppointment } from "@/types/self-schedule";
import { selfScheduleMutationOptions, selfScheduleQueryKeys } from "@/api/check-in/self-schedule/self-schedule-query";
import { FIRST_AVAILABLE_PROVIDER_ID, SelfScheduleStep, useSelfScheduleStore } from "@/stores/self-schedule-store";
interface CheckoutProps {
  className?: string;
}
const Checkout = t0 => {
  const $ = _c(74);
  const {
    className
  } = t0;
  const {
    selectedServices,
    setCreatedAppointmentId,
    setPaymentSuccessful,
    resetAll,
    selectedDateTime,
    bookingLocationId,
    selectedProvider,
    selectedAvailabilityAssignments,
    clearSelectedDateTime,
    setCurrentStep,
    cancelEventId,
    createdAppointmentId,
    rwgToken,
    rwgMerchantId,
    appliedCarePlanStepId,
    setAppliedCarePlanStepId,
    bookingEventId,
    waitlistId,
    serviceInstanceProviderIds,
    addonProviderIds,
    isMultiProviderSelection,
    usesPerOccurrenceProviderSelection,
    patientNotesEnabled,
    patientNote
  } = useSelfScheduleContext();
  const {
    claimedPatient: patient
  } = usePatientClaim();
  const patientDetails = useSelfSchedulePatientDetails();
  const detailsReady = patientDetails?.ready ?? true;
  const setPatient = useUpdateClaimedPatient();
  const {
    selectedAddons,
    selectedAvailabilityWindow
  } = useSelfScheduleStore();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = selfScheduleMutationOptions.selfScheduleAppointment();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== appliedCarePlanStepId || $[2] !== cancelEventId || $[3] !== patient || $[4] !== setAppliedCarePlanStepId || $[5] !== setCreatedAppointmentId || $[6] !== setPatient || $[7] !== setPaymentSuccessful) {
    let t3;
    if ($[9] !== setAppliedCarePlanStepId) {
      t3 = async error => {
        setAppliedCarePlanStepId(null);
        const errorMessage = await parseError(error);
        toast({
          title: "Error",
          description: typeof errorMessage === "string" ? errorMessage : "Failed to secure your appointment. Please try again.",
          variant: "error"
        });
      };
      $[9] = setAppliedCarePlanStepId;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = {
      ...t1,
      onSuccess(data) {
        if (data) {
          setCreatedAppointmentId(data.id);
          if (cancelEventId && data.status === EventStatus.PENDING) {
            setPaymentSuccessful(true);
          }
        }
        queryClient.removeQueries({
          queryKey: selfScheduleQueryKeys.allAvailability()
        });
        if (patient && data) {
          const carePlanAppointmentWasBooked = !!appliedCarePlanStepId && patient.nextCarePlanAppointment?.stepId === appliedCarePlanStepId;
          setPatient(p => p ? {
            ...p,
            futureAppointments: [...p.futureAppointments.filter(a => a.id !== cancelEventId), toPublicEventTiny(data)],
            nextCarePlanAppointment: carePlanAppointmentWasBooked ? null : p.nextCarePlanAppointment
          } : p);
        }
        setAppliedCarePlanStepId(null);
      },
      onError: t3
    };
    $[1] = appliedCarePlanStepId;
    $[2] = cancelEventId;
    $[3] = patient;
    $[4] = setAppliedCarePlanStepId;
    $[5] = setCreatedAppointmentId;
    $[6] = setPatient;
    $[7] = setPaymentSuccessful;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    mutate: createAppointment,
    isPending,
    error: error_0
  } = useMutation(t2);
  const hasAttemptedCreation = useRef(false);
  let t3;
  if ($[11] !== addonProviderIds || $[12] !== appliedCarePlanStepId || $[13] !== bookingEventId || $[14] !== bookingLocationId || $[15] !== cancelEventId || $[16] !== clearSelectedDateTime || $[17] !== createAppointment || $[18] !== createdAppointmentId || $[19] !== detailsReady || $[20] !== isMultiProviderSelection || $[21] !== isPending || $[22] !== patient || $[23] !== patientNote || $[24] !== patientNotesEnabled || $[25] !== rwgMerchantId || $[26] !== rwgToken || $[27] !== selectedAddons || $[28] !== selectedAvailabilityAssignments || $[29] !== selectedAvailabilityWindow || $[30] !== selectedDateTime || $[31] !== selectedProvider || $[32] !== selectedServices || $[33] !== serviceInstanceProviderIds || $[34] !== setCurrentStep || $[35] !== usesPerOccurrenceProviderSelection || $[36] !== waitlistId) {
    t3 = () => {
      if (!detailsReady) {
        return;
      }
      if (createdAppointmentId) {
        hasAttemptedCreation.current = true;
        return;
      }
      if (!!selectedServices?.length && selectedDateTime && bookingLocationId && patient?.id && !hasAttemptedCreation.current && !isPending) {
        const totalDuration = computeTotalDuration(selectedServices, selectedAddons);
        const allServiceIds = flattenServiceIds(selectedServices, selectedAddons);
        const hasMatchingAvailabilityAssignments = selectedAvailabilityAssignments.length === allServiceIds.length && selectedAvailabilityAssignments.every((assignment, index) => assignment.serviceId === allServiceIds[index]);
        if (!hasMatchingAvailabilityAssignments) {
          clearSelectedDateTime();
          setCurrentStep(SelfScheduleStep.TIME, "backward");
          toast({
            title: "Please choose your time again",
            description: "The providers for your selected time could not be confirmed.",
            variant: "error"
          });
          return;
        }
        const addonSelections = Object.fromEntries(resolveServiceOccurrences(selectedServices, selectedAddons).filter(_temp).map(_temp2));
        const patientSelectedSpecificProvider = usesPerOccurrenceProviderSelection || isMultiProviderSelection ? [...Object.values(serviceInstanceProviderIds), ...Object.values(addonProviderIds ?? {})].some(_temp3) : !!selectedProvider;
        const selfScheduleServiceData = {
          assignments: selectedAvailabilityAssignments,
          organizerId: patient.id,
          providerRequestedByPatient: !cancelEventId && patientSelectedSpecificProvider,
          locationId: bookingLocationId,
          start: selectedDateTime,
          end: selectedAvailabilityWindow && new Date(selectedAvailabilityWindow.start).getTime() === selectedDateTime.getTime() ? new Date(selectedAvailabilityWindow.end) : addMinutes(selectedDateTime, totalDuration),
          cancelEventId,
          addonSelections: Object.keys(addonSelections).length > 0 ? addonSelections : undefined,
          carePlanStepId: appliedCarePlanStepId ?? undefined,
          bookingEventId: bookingEventId ?? undefined,
          waitlistId,
          rwgToken,
          rwgMerchantId,
          marketing: getMarketingBundle(),
          notes: patientNotesEnabled && patientNote.trim() || undefined
        };
        hasAttemptedCreation.current = true;
        createAppointment(selfScheduleServiceData);
      }
    };
    $[11] = addonProviderIds;
    $[12] = appliedCarePlanStepId;
    $[13] = bookingEventId;
    $[14] = bookingLocationId;
    $[15] = cancelEventId;
    $[16] = clearSelectedDateTime;
    $[17] = createAppointment;
    $[18] = createdAppointmentId;
    $[19] = detailsReady;
    $[20] = isMultiProviderSelection;
    $[21] = isPending;
    $[22] = patient;
    $[23] = patientNote;
    $[24] = patientNotesEnabled;
    $[25] = rwgMerchantId;
    $[26] = rwgToken;
    $[27] = selectedAddons;
    $[28] = selectedAvailabilityAssignments;
    $[29] = selectedAvailabilityWindow;
    $[30] = selectedDateTime;
    $[31] = selectedProvider;
    $[32] = selectedServices;
    $[33] = serviceInstanceProviderIds;
    $[34] = setCurrentStep;
    $[35] = usesPerOccurrenceProviderSelection;
    $[36] = waitlistId;
    $[37] = t3;
  } else {
    t3 = $[37];
  }
  const t4 = patient?.id;
  let t5;
  if ($[38] !== addonProviderIds || $[39] !== bookingEventId || $[40] !== bookingLocationId || $[41] !== clearSelectedDateTime || $[42] !== createAppointment || $[43] !== createdAppointmentId || $[44] !== detailsReady || $[45] !== isMultiProviderSelection || $[46] !== isPending || $[47] !== patientNote || $[48] !== patientNotesEnabled || $[49] !== selectedAddons || $[50] !== selectedAvailabilityAssignments || $[51] !== selectedAvailabilityWindow || $[52] !== selectedDateTime || $[53] !== selectedProvider || $[54] !== selectedServices || $[55] !== serviceInstanceProviderIds || $[56] !== setCurrentStep || $[57] !== t4 || $[58] !== usesPerOccurrenceProviderSelection || $[59] !== waitlistId) {
    t5 = [selectedServices, selectedDateTime, bookingLocationId, t4, detailsReady, createAppointment, isPending, createdAppointmentId, selectedProvider, serviceInstanceProviderIds, addonProviderIds, usesPerOccurrenceProviderSelection, isMultiProviderSelection, selectedAvailabilityAssignments, selectedAvailabilityWindow, selectedAddons, clearSelectedDateTime, setCurrentStep, bookingEventId, waitlistId, patientNotesEnabled, patientNote];
    $[38] = addonProviderIds;
    $[39] = bookingEventId;
    $[40] = bookingLocationId;
    $[41] = clearSelectedDateTime;
    $[42] = createAppointment;
    $[43] = createdAppointmentId;
    $[44] = detailsReady;
    $[45] = isMultiProviderSelection;
    $[46] = isPending;
    $[47] = patientNote;
    $[48] = patientNotesEnabled;
    $[49] = selectedAddons;
    $[50] = selectedAvailabilityAssignments;
    $[51] = selectedAvailabilityWindow;
    $[52] = selectedDateTime;
    $[53] = selectedProvider;
    $[54] = selectedServices;
    $[55] = serviceInstanceProviderIds;
    $[56] = setCurrentStep;
    $[57] = t4;
    $[58] = usesPerOccurrenceProviderSelection;
    $[59] = waitlistId;
    $[60] = t5;
  } else {
    t5 = $[60];
  }
  useEffect(t3, t5);
  let t6;
  let t7;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      hasAttemptedCreation.current = false;
    };
    t7 = [];
    $[61] = t6;
    $[62] = t7;
  } else {
    t6 = $[61];
    t7 = $[62];
  }
  useEffect(t6, t7);
  if (!selectedServices?.length) {
    return null;
  }
  let t8;
  if ($[63] !== className) {
    t8 = cx("motion-ease-spring-bouncy", "motion-scale-in-105 motion-opacity-in-85", className);
    $[63] = className;
    $[64] = t8;
  } else {
    t8 = $[64];
  }
  let t9;
  if ($[65] !== isPending) {
    t9 = isPending && <div className="flex flex-col items-center justify-center space-y-16"><h1 className="mb-2 mt-12 text-center text-h1 text-foreground">Please wait while we secure your spot...</h1><LoadingSpinner /></div>;
    $[65] = isPending;
    $[66] = t9;
  } else {
    t9 = $[66];
  }
  let t10;
  if ($[67] !== error_0 || $[68] !== resetAll) {
    t10 = error_0 && <div className="flex flex-col justify-center space-y-4"><div><h1 className="mb-2 mt-12 text-center text-h1 text-foreground">Error securing your spot.</h1><p className="text-center text-foreground-secondary">Please try again or contact us.</p></div><Button onClick={() => {
        hasAttemptedCreation.current = false;
        resetAll();
      }} variant="primary"><span>Try again</span></Button></div>;
    $[67] = error_0;
    $[68] = resetAll;
    $[69] = t10;
  } else {
    t10 = $[69];
  }
  let t11;
  if ($[70] !== t10 || $[71] !== t8 || $[72] !== t9) {
    t11 = <div data-testid="step-secure-appointment" className={t8} key="secure-appointment-container">{t9}{t10}</div>;
    $[70] = t10;
    $[71] = t8;
    $[72] = t9;
    $[73] = t11;
  } else {
    t11 = $[73];
  }
  return t11;
};
export default Checkout;
function _temp(t0) {
  const {
    addonIds
  } = t0;
  return addonIds.length > 0;
}
function _temp2(t0) {
  const {
    occurrenceKey,
    addonIds: addonIds_0
  } = t0;
  return [occurrenceKey, addonIds_0];
}
function _temp3(providerId) {
  return providerId !== FIRST_AVAILABLE_PROVIDER_ID;
}